import { ExtractResponse, type Fiche, type Language, type TemplateId } from '@pharmafiche/schema';

/** Friendly French messages for common server error codes. */
const ERROR_MESSAGES: Record<string, string> = {
  invalid_request: 'Requête invalide. Réessaie ou contacte le support.',
  rate_limited:
    'Limite horaire atteinte. Patiente quelques minutes avant de relancer la génération.',
  ai_invalid_response:
    'Le modèle a renvoyé un format invalide. Réessaie — parfois reformuler tes sources aide.',
  config_missing: "Le service IA n'est pas configuré. Le développeur doit pousser la clé Gemini.",
  not_implemented: 'Fonctionnalité pas encore disponible — phase suivante.',
  internal_error: 'Échec côté serveur. Réessaie dans quelques instants.',
};

export class AiClientError extends Error {
  constructor(
    public code: string,
    public friendly: string,
    public httpStatus: number,
    public details?: unknown,
  ) {
    super(friendly);
    this.name = 'AiClientError';
  }
}

export interface ExtractParams {
  blob: string;
  language: Language;
  template_hint?: TemplateId;
}

export interface ExtractOutcome {
  fiche: Fiche;
  model_used: string;
  latency_ms: number;
}

export async function extractFiche(params: ExtractParams): Promise<ExtractOutcome> {
  let res: Response;
  try {
    res = await fetch('/api/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        blob: params.blob,
        language: params.language,
        ...(params.template_hint ? { template_hint: params.template_hint } : {}),
      }),
    });
  } catch (err) {
    throw new AiClientError(
      'network',
      'Connexion impossible. Vérifie ta connexion internet.',
      0,
      err instanceof Error ? err.message : undefined,
    );
  }

  if (!res.ok) {
    let code = 'internal_error';
    let details: unknown;
    try {
      const data = (await res.json()) as { error?: { code?: string; details?: unknown } };
      if (data.error?.code) code = data.error.code;
      details = data.error?.details;
    } catch {
      // body wasn't JSON
    }
    const friendly = ERROR_MESSAGES[code] ?? `Erreur ${res.status}`;
    throw new AiClientError(code, friendly, res.status, details);
  }

  const data = (await res.json()) as unknown;
  const parsed = ExtractResponse.safeParse(data);
  if (!parsed.success) {
    throw new AiClientError(
      'client_parse',
      "La réponse du serveur n'est pas reconnue. Réessaie.",
      200,
      parsed.error.issues.slice(0, 3),
    );
  }

  return {
    fiche: parsed.data.data,
    model_used: parsed.data.model_used,
    latency_ms: parsed.data.latency_ms,
  };
}
